<template>
    <div
        id="app"
        class="h-100"
        :class="[skinClasses]"
    >
        <warning-download-app />
        <component :is="layout" />
    </div>
</template>

<script>

import {$themeBreakpoints, $themeColors, $themeConfig} from '@themeConfig'
import {provideToast} from 'vue-toastification/composition'
import {watch} from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import {useCssVar, useWindowSize} from '@vueuse/core'
import store from '@/store'
import WarningDownloadApp from "@/layouts/components/WarningDownloadApp.vue";
import formStoreModule from '@/velta/components/crud/mixins/storeModule'
import filterStoreModule from '@/velta/components/filter/mixins/storeModule'
import Swal from 'sweetalert2/dist/sweetalert2.js'

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')
const LayoutCatalogoVirtual = () => import('@/layouts/CatalogoVirtual.vue')
const LayoutOnboarding = () => import('@/layouts/onboarding/LayoutOnboarding.vue')

export default {
    components: {
        WarningDownloadApp,
        LayoutHorizontal,
        LayoutVertical,
        LayoutFull,
        LayoutCatalogoVirtual,
        LayoutOnboarding,
    },
    computed: {
        layout() {
            if (this.$route.meta.layout === 'catalogo-virtual') {
                return 'layout-catalogo-virtual'
            }
            if (this.$route.meta.layout === 'onboarding') {
                return 'layout-onboarding'
            }
            return (this.$route.meta.layout === 'full') ? 'layout-full' : `layout-${this.contentLayoutType}`
        },
        contentLayoutType() {
            return this.$store.state.appConfig.layout.type
        },
    },
    beforeCreate() {
        const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

        for (let i = 0, len = colors.length; i < len; i++) {
            $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
        }
        const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

        for (let i = 0, len = breakpoints.length; i < len; i++) {
            $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
        }

        const { isRTL } = $themeConfig.layout
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')
    },
    mounted() {
        this.showMaintenancePopup()
    },
    methods: {
        showMaintenancePopup() {
            const STORAGE_KEY = 'veltacar_melhorias_mar2026_seen'
            if (localStorage.getItem(STORAGE_KEY)) return

            const now = new Date()
            const start = new Date('2026-04-10T00:00:00')
            const end = new Date('2026-04-16T23:59:59')
            if (now < start || now > end) return

            Swal.fire({
                icon: 'info',
                title: 'Estamos melhorando o VeltaCar! 🚀',
                html: `
                    <div style="text-align: left; font-size: 14px; line-height: 1.7;">
                        <p>Olá! Estamos realizando <strong>diversas melhorias</strong> para deixar o VeltaCar mais <strong>moderno e fácil de usar</strong>.</p>
                        <p>Durante os próximos dias, algumas atualizações estarão sendo aplicadas. Caso você perceba qualquer comportamento diferente no sistema, principalmente em relação a <strong>permissões</strong> ou <strong>assinaturas</strong>, por favor nos avise!</p>
                        <p style="margin-bottom: 0;">Nossa equipe estará <strong>atenta e pronta</strong> para solucionar qualquer questão o mais rápido possível. 😊</p>
                    </div>
                `,
                confirmButtonText: 'Entendi!',
                confirmButtonColor: '#7367f0',
                width: 520,
                customClass: {
                    popup: 'rounded',
                },
            }).then(function() {
                localStorage.setItem(STORAGE_KEY, '1')
            })
        },
    },
    setup() {
        const { skinClasses } = useAppConfig()

        provideToast({
            hideProgressBar: true,
            closeOnClick: false,
            closeButton: false,
            icon: false,
            timeout: 3000,
            transition: 'Vue-Toastification__fade',
        })

        store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
        const { width: windowWidth } = useWindowSize()
        watch(windowWidth, val => {
            store.commit('app/UPDATE_WINDOW_WIDTH', val)
        })

        try {
            const formModules = [
                "controlecaixacategoria",
                "itemchecklistmodelo",
                "cliente",
                "convenio",
                "formasdepagamentos",
                "funcionario",
                "empresaCliente",
                "modalidade",
                "perfil",
                "produto",
                "servico",
                "usuario",
                "vaga",
                "controlecaixa",
                "orcamento",
                "venda",
                "recibo",
                "adminveltaposts",
                "adminveltanotificacoes"
            ]

            formModules.forEach(view => {
                try {
                    formStoreModule(view)
                } catch (error) {
                    console.error(`Erro ao registrar módulo de form ${view}:`, error)
                }
            })

            const filterModules = [
                "relatoriovenda",
                "aniversariante",
                "caixa",
                "relatoriocliente",
                "relatorioestoque",
                "servicorealizado",
                "servicoposvendacliente"
            ]

            filterModules.forEach(view => {
                try {
                    filterStoreModule(view)
                } catch (error) {
                    console.error(`Erro ao registrar módulo de filter ${view}:`, error)
                }
            })
        } catch (error) {
            console.error('Erro ao registrar módulos:', error)
        }

        return {
            skinClasses,
        }
    },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>
